//#region src/utils/schema-aml-parser/tokenizer.ts
var TokenKind = {
	identifier: 1,
	quoted: 2,
	string: 3,
	expression: 4,
	number: 5,
	punctuation: 6,
	doc: 7,
	comment: 8,
	newline: 9
};
var SPACE = /[ \t\f\v]/;
var DIGIT = /[0-9]/;
var IDENTIFIER_START = /[A-Za-z_\u0080-\uffff]/;
var IDENTIFIER_PART = /[0-9A-Za-z_#\u0080-\uffff]/;
var INDENT = /^[ \t]*/;
var ESCAPED_HASH = /\\#/g;
var ESCAPE_MAP = { n: "\n" };
function tokenize(source) {
	const tokens = [];
	let index = 0;
	let line = 1;
	let depth = indentDepth(source, 0);
	const push = (kind, value) => {
		tokens.push({
			kind,
			value,
			line,
			depth
		});
	};
	tokens.push({
		kind: TokenKind.newline,
		value: "",
		line: 0,
		depth
	});
	while (index < source.length) {
		const char = source[index];
		if (SPACE.test(char)) {
			index += 1;
			continue;
		}
		if (char === "\r" || char === "\n") {
			if (char === "\r" && source[index + 1] === "\n") index += 1;
			index += 1;
			depth = indentDepth(source, index);
			push(TokenKind.newline, "\n");
			line += 1;
			continue;
		}
		if (char === "#") {
			let end = index + 1;
			while (end < source.length && source[end] !== "\n" && source[end] !== "\r") end += 1;
			push(TokenKind.comment, source.slice(index + 1, end).trim());
			index = end;
			continue;
		}
		if (char === "|") {
			if (source.startsWith("|||", index)) {
				const result = readMultilineDoc(source, index + 3);
				push(TokenKind.doc, dedent(result.value));
				index = result.index;
				line += result.lines;
				continue;
			}
			const result = readDoc(source, index + 1);
			push(TokenKind.doc, result.value);
			index = result.index;
			continue;
		}
		if (char === "\"") {
			const result = readDelimited(source, index + 1, "\"", true, false);
			push(TokenKind.quoted, result.value);
			index = result.index;
			continue;
		}
		if (char === "'") {
			const result = readDelimited(source, index + 1, "'", true, false);
			push(TokenKind.string, result.value);
			index = result.index;
			continue;
		}
		if (char === "`") {
			const result = readDelimited(source, index + 1, "`", false, true);
			push(TokenKind.expression, result.value);
			index = result.index;
			line += result.lines;
			continue;
		}
		if (DIGIT.test(char)) {
			let end = index;
			while (end < source.length && DIGIT.test(source[end])) end += 1;
			if (source[end] === "." && DIGIT.test(source[end + 1] ?? "")) {
				end += 1;
				while (end < source.length && DIGIT.test(source[end])) end += 1;
			}
			push(TokenKind.number, source.slice(index, end));
			index = end;
			continue;
		}
		if (IDENTIFIER_START.test(char)) {
			let end = index + 1;
			while (end < source.length && IDENTIFIER_PART.test(source[end])) end += 1;
			while (end > index && source[end - 1] === "#") end -= 1;
			push(TokenKind.identifier, source.slice(index, end));
			index = end;
			continue;
		}
		push(TokenKind.punctuation, char);
		index += 1;
	}
	return tokens;
}
function indentDepth(source, index) {
	let width = 0;
	for (let i = index; i < source.length; i++) {
		const char = source[i];
		if (char === "	") width += 1;
		else if (char === " ") width += .5;
		else break;
	}
	return Math.round(width) - 1;
}
function readDoc(source, start) {
	let index = start;
	while (index < source.length && SPACE.test(source[index])) index += 1;
	if (index > start && source[index] === "\"") {
		const quoted = readDelimited(source, index + 1, "\"", true, false);
		if (quoted.terminated) return {
			value: quoted.value,
			index: quoted.index
		};
	}
	const buffer = [];
	index = start;
	while (index < source.length) {
		const char = source[index];
		if (char === "\n" || char === "\r") break;
		if (char !== " " && source[index + 1] === "#") {
			buffer.push(char, "#");
			index += 2;
			continue;
		}
		if (char === "#") break;
		buffer.push(char);
		index += 1;
	}
	return {
		value: unquote(buffer.join("").trim().replace(ESCAPED_HASH, "#")),
		index
	};
}
function readMultilineDoc(source, start) {
	const closing = source.indexOf("|||", start);
	const end = closing === -1 ? source.length : closing;
	const value = source.slice(start, end);
	return {
		value,
		index: closing === -1 ? source.length : closing + 3,
		lines: value.split("\n").length - 1
	};
}
function readDelimited(source, start, closing, escaped, multiline) {
	const buffer = [];
	let index = start;
	let lines = 0;
	while (index < source.length) {
		if (source.startsWith(closing, index)) return {
			value: buffer.join(""),
			index: index + closing.length,
			lines,
			terminated: true
		};
		const char = source[index];
		if (!multiline && (char === "\n" || char === "\r")) break;
		const next = source[index + 1];
		if (escaped && char === "\\" && next !== void 0) {
			buffer.push(ESCAPE_MAP[next] ?? next);
			index += 2;
			continue;
		}
		if (char === "\n") lines += 1;
		buffer.push(char);
		index += 1;
	}
	return {
		value: buffer.join(""),
		index,
		lines,
		terminated: false
	};
}
function unquote(value) {
	const quote = value[0];
	return value.length > 1 && (quote === "\"" || quote === "'") && value.endsWith(quote) ? value.slice(1, -1) : value;
}
function dedent(value) {
	const lines = value.replace(/\r\n|\r/g, "\n").split("\n");
	if (lines[0]?.trim() === "") lines.shift();
	if (lines.length && lines[lines.length - 1].trim() === "") lines.pop();
	const indents = lines.filter((line) => line.trim() !== "").map((line) => (INDENT.exec(line)?.[0] ?? "").length);
	const common = indents.length ? Math.min(...indents) : 0;
	return lines.map((line) => line.slice(common)).join("\n");
}
//#endregion
export { TokenKind, tokenize };
